import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Room,
  RoomEvent,
  Track,
  ConnectionState,
  DataPacket_Kind,
  createLocalAudioTrack,
  ParticipantEvent
} from 'livekit-client';

// External API Service for token generation
const TokenService = {
  // Backend API URL - replace with your actual backend URL
  BACKEND_API_URL: process.env.REACT_APP_API_URL || "http://localhost:3323",
  
  /**
   * Get connection details including a token from the server
   */
  async getConnectionDetails() {
    try {
      // Make an API call to your backend endpoint
      const response = await fetch(`${this.BACKEND_API_URL}/livekit/token`);
      
      if (!response.ok) {
        throw new Error(`Failed to get token: ${response.status} ${response.statusText}`);
      }
      
      // Parse and return the connection details
      const connectionDetails = await response.json();
      return connectionDetails;
    } catch (error) {
      console.error("Error getting token:", error);
      throw error;
    }
  }
};

const CallbotSupportAgent = () => {
  const navigate = useNavigate();
  const [callStatus, setCallStatus] = useState('idle'); // idle, calling, connected, ended
  const [callDuration, setCallDuration] = useState(0);
  const [callEvents, setCallEvents] = useState([]);
  const [isBotSpeaking, setIsBotSpeaking] = useState(false);
  const [isUserSpeaking, setIsUserSpeaking] = useState(false);
  const [botAudioLevel, setBotAudioLevel] = useState(0);
  const [userAudioLevel, setUserAudioLevel] = useState(0);
  const [callAccepted, setCallAccepted] = useState(false);
  
  const audioVisualizerRef = useRef(null);
  const timerRef = useRef(null);
  const roomRef = useRef(null);
  const localAudioRef = useRef(null);
  const remoteAudioRef = useRef(null);
  const beepSoundRef = useRef(null);
  const beepIntervalRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const dataArrayRef = useRef(null);
  const animationFrameRef = useRef(null);
  
  // LiveKit connection parameters
  const [livekitUrl, setLivekitUrl] = useState('');
  const [roomName, setRoomName] = useState('');
  const [token, setToken] = useState(null);
  const [participantName, setParticipantName] = useState(null);
  const [isTokenLoading, setIsTokenLoading] = useState(false);

  useEffect(() => {
    // Set up audio context for analyzing local audio
    if (window.AudioContext || window.webkitAudioContext) {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      analyserRef.current = audioContextRef.current.createAnalyser();
      analyserRef.current.fftSize = 256;
      const bufferLength = analyserRef.current.frequencyBinCount;
      dataArrayRef.current = new Uint8Array(bufferLength);
    }
    
    // Create audio element for the beep sound
    beepSoundRef.current = new Audio();
    beepSoundRef.current.src = '/assets/sounds/call-connect-beep.mp3';
    beepSoundRef.current.preload = 'auto';
    
    // Cleanup function to handle component unmount
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      if (roomRef.current) {
        disconnectFromRoom();
      }
      if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
        audioContextRef.current.close();
      }
      stopBeepSound();
      
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
    };
  }, []);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const addCallEvent = (type, message) => {
    setCallEvents(prev => [...prev, {
      type,
      message,
      timestamp: Date.now()
    }]);
  };

  const startBeepSound = () => {
    stopBeepSound();
    
    if (beepSoundRef.current && beepSoundRef.current.src) {
      beepSoundRef.current.play().catch(err => {
        console.warn("Could not play beep sound:", err);
      });
      
      beepIntervalRef.current = setInterval(() => {
        beepSoundRef.current.currentTime = 0;
        beepSoundRef.current.play().catch(err => {
          console.warn("Could not play beep sound:", err);
        });
      }, 2000);
    }
  };

  const stopBeepSound = () => {
    if (beepIntervalRef.current) {
      clearInterval(beepIntervalRef.current);
      beepIntervalRef.current = null;
    }
    
    if (beepSoundRef.current) {
      beepSoundRef.current.pause();
      beepSoundRef.current.currentTime = 0;
    }
  };

  const checkMicrophoneAccess = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach(track => track.stop());
      return true;
    } catch (error) {
      console.error("Microphone access denied or not available:", error);
      return false;
    }
  };

  const updateUserAudioVisualization = () => {
    if (!analyserRef.current || !dataArrayRef.current) return;
    
    analyserRef.current.getByteFrequencyData(dataArrayRef.current);
    
    let sum = 0;
    for (let i = 0; i < dataArrayRef.current.length; i++) {
      sum += dataArrayRef.current[i];
    }
    const average = sum / dataArrayRef.current.length;
    
    const normalizedLevel = Math.min(1, average / 128);
    setUserAudioLevel(normalizedLevel);
    
    const VOLUME_THRESHOLD = 10;
    setIsUserSpeaking(average > VOLUME_THRESHOLD);
    
    animationFrameRef.current = requestAnimationFrame(updateUserAudioVisualization);
  };

  const getToken = async () => {
    setIsTokenLoading(true);
    try {
      const connectionDetails = await TokenService.getConnectionDetails();
      
      setToken(connectionDetails.participantToken);
      setParticipantName(connectionDetails.participantName);
      setLivekitUrl(connectionDetails.serverUrl);
      setRoomName(connectionDetails.roomName);
      
      setIsTokenLoading(false);
      return connectionDetails;
    } catch (error) {
      console.error('Error getting token:', error);
      addCallEvent('system', "Error getting connection token. Please try again later.");
      setIsTokenLoading(false);
      stopBeepSound();
      return null;
    }
  };

  const startCall = async () => {
    setCallStatus('calling');
    addCallEvent('system', "Connecting to Ooredoo support agent...");
    startBeepSound();
    
    try {
      const hasMicAccess = await checkMicrophoneAccess();
      if (!hasMicAccess) {
        console.warn("Microphone access denied, but continuing with call in simulated mode");
        addCallEvent('system', "Microphone access not available. Using simulated voice mode.");
      }
      
      const connectionDetails = await getToken();
      if (!connectionDetails) {
        throw new Error('Failed to get token');
      }
      
      await connectToRoom(connectionDetails);
      
      timerRef.current = setInterval(() => {
        setCallDuration(prev => prev + 1);
      }, 1000);
      
      stopBeepSound();
    } catch (error) {
      console.error('Failed to connect to LiveKit room:', error);
      addCallEvent('system', "Failed to connect. Please try again later.");
      setCallStatus('ended');
      stopBeepSound();
    }
  };

  const connectToRoom = async (connectionDetails) => {
    if (!connectionDetails || !connectionDetails.participantToken) {
      throw new Error('No token available');
    }
    
    try {
      const room = new Room({
        adaptiveStream: true,
        dynacast: true,
        audioOnly: true
      });
      roomRef.current = room;
      
      setupRoomListeners(room);
      
      await room.connect(connectionDetails.serverUrl, connectionDetails.participantToken);
      
      stopBeepSound();
      
      try {
        const localAudio = await createLocalAudioTrack({
          name: 'microphone',
        });
        localAudioRef.current = localAudio;
        
        if (audioContextRef.current && analyserRef.current) {
          const source = audioContextRef.current.createMediaStreamSource(
            new MediaStream([localAudio.mediaStreamTrack])
          );
          source.connect(analyserRef.current);
          animationFrameRef.current = requestAnimationFrame(updateUserAudioVisualization);
        }
        
        await room.localParticipant.publishTrack(localAudio);
      } catch (mediaError) {
        console.warn('Could not access microphone, using simulated audio:', mediaError);
        addCallEvent('system', "Microphone not available. Using simulated voice mode.");
      }
      
      addCallEvent('system', `You (${connectionDetails.participantName || 'User'}) joined the call.`);
      setCallStatus('connected');
    } catch (error) {
      console.error('Error connecting to room:', error);
      stopBeepSound();
      throw error;
    }
  };

  const setupRoomListeners = (room) => {
    room.on(RoomEvent.ConnectionStateChanged, (state) => {
      if (state === ConnectionState.Connected) {
        stopBeepSound();
      } else if (state === ConnectionState.Disconnected) {
        if (callStatus !== 'ended') {
          endCall();
        }
        stopBeepSound();
      }
    });
    
    room.on(RoomEvent.ParticipantConnected, (participant) => {
      if (participant.identity.includes('agent')) {
        addCallEvent('system', "Ooredoo AI agent has connected to the call.");
        setCallAccepted(true);
        
        participant.on(ParticipantEvent.IsSpeakingChanged, (isSpeaking) => {
          setIsBotSpeaking(isSpeaking);
          
          if (isSpeaking) {
            setBotAudioLevel(0.7);
            
            const animateBotLevel = () => {
              if (roomRef.current && roomRef.current.connectionState === ConnectionState.Connected) {
                const randomLevel = 0.5 + Math.random() * 0.5;
                setBotAudioLevel(randomLevel);
                
                if (isSpeaking) {
                  setTimeout(animateBotLevel, 150);
                }
              }
            };
            
            animateBotLevel();
          } else {
            setBotAudioLevel(0);
          }
        });
        
        participant.on('audioLevelChanged', (level) => {
          if (level > 0.01) {
            const scaledLevel = Math.min(1, level * 15);
            setBotAudioLevel(scaledLevel);
          }
        });
      }
    });
    
    room.on(RoomEvent.ParticipantDisconnected, (participant) => {
      if (participant.identity.includes('agent')) {
        endCall();
      }
    });
    
    room.on(RoomEvent.TrackSubscribed, (track, publication, participant) => {
      if (track.kind === Track.Kind.Audio) {
        const audioElement = new Audio();
        audioElement.srcObject = new MediaStream([track.mediaStreamTrack]);
        audioElement.play();
        remoteAudioRef.current = audioElement;
      }
    });
    
    room.on(RoomEvent.DataReceived, (data, participant) => {
      try {
        const message = JSON.parse(new TextDecoder().decode(data));
      } catch (error) {
        console.error('Error processing received data:', error);
      }
    });
    
    room.on(RoomEvent.MediaDevicesError, (error) => {
      console.error('Media device error:', error);
      addCallEvent('system', "There was an issue with your microphone. Please check your device settings.");
    });
  };

  const disconnectFromRoom = async () => {
    try {
      stopBeepSound();
      
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      
      if (roomRef.current) {
        if (localAudioRef.current) {
          localAudioRef.current.stop();
          localAudioRef.current = null;
        }
        
        await roomRef.current.disconnect(true);
        roomRef.current = null;
      }
      
      if (remoteAudioRef.current) {
        remoteAudioRef.current.pause();
        remoteAudioRef.current.srcObject = null;
        remoteAudioRef.current = null;
      }
    } catch (error) {
      console.error('Error disconnecting from room:', error);
    } finally {
      setCallStatus('ended');
      setCallAccepted(false);
    }
  };

  const endCall = async () => {
    addCallEvent('system', "Ending call...");
    
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    
    await disconnectFromRoom();
    stopBeepSound();
    addCallEvent('system', "Call ended. Thank you for using Ooredoo voice assistant.");
  };

  return (
    <div className="demo-page-container">
      <div className="demo-page-header">
        <h2>Customer Support Agent (Callbot)</h2>
      </div>
      
      <div className="demo-page-content">
        <div className="callbot-container">
          <div className="phone-interface">
            <div className="phone-screen">
              <div className="call-header">
                <img src="/assets/img/Ooredoo_logo.svg" alt="Ooredoo" className="call-avatar" />
                <h4>Ooredoo Support</h4>
                <p className="call-status">
                  {callStatus === 'idle' && 'Ready to call'}
                  {callStatus === 'calling' && callAccepted ? 'Agent connected' : 'Calling...'}
                  {callStatus === 'connected' && 'In call'}
                  {callStatus === 'ended' && 'Call ended'}
                </p>
              </div>
              
              <div className="call-duration">
                {formatTime(callDuration)}
              </div>
              
              <div className="audio-visualizer">
                {callStatus === 'connected' && (
                  <div className="voice-visualizer">
                    <div className={`bot-visualizer ${isBotSpeaking ? 'speaking' : ''}`}>
                      {Array.from({ length: 10 }).map((_, i) => (
                        <div 
                          key={`bot-bar-${i}`} 
                          className="visualizer-bar bot-bar"
                          style={{ 
                            height: isBotSpeaking 
                              ? `${5 + (botAudioLevel * 50 * (0.6 + (Math.sin(i * 0.8) * 0.4)))}px` 
                              : '5px',
                            opacity: isBotSpeaking ? 1 : 0.5,
                            animation: isBotSpeaking ? 'pulse 0.5s infinite alternate' : 'none',
                            transition: 'height 0.1s ease-in-out'
                          }}
                        />
                      ))}
                    </div>
                    
                    <div className={`user-visualizer ${isUserSpeaking ? 'speaking' : ''}`}>
                      {Array.from({ length: 10 }).map((_, i) => (
                        <div 
                          key={`user-bar-${i}`} 
                          className="visualizer-bar user-bar"
                          style={{ 
                            height: isUserSpeaking 
                              ? `${5 + (userAudioLevel * 40 * (0.7 + (Math.sin(i) * 0.3)))}px` 
                              : '5px',
                            opacity: isUserSpeaking ? 1 : 0.5,
                            animation: isUserSpeaking ? 'pulse 0.5s infinite alternate' : 'none'
                          }}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>
              
              <div className="call-events">
                {callEvents.slice(-3).map((event, index) => (
                  <p key={index} className={`event-${event.type}`}>
                    {event.message}
                  </p>
                ))}
              </div>
            </div>
            
            <div className="phone-controls">
              {callStatus === 'idle' && (
                <button className="call-button" onClick={startCall} disabled={isTokenLoading}>
                  <span className="material-icons">call</span> 
                  {isTokenLoading ? 'Preparing Call...' : 'Start Call'}
                </button>
              )}
              
              {callStatus === 'calling' && (
                <button className="end-button" onClick={endCall}>
                  <span className="material-icons">call_end</span> Cancel
                </button>
              )}
              
              {callStatus === 'connected' && (
                <>
                  <button className="end-button" onClick={endCall}>
                    <span className="material-icons">call_end</span> End Call
                  </button>
                </>
              )}
              
              {callStatus === 'ended' && (
                <button className="call-button" onClick={() => {
                  setCallStatus('idle');
                  setCallDuration(0);
                  setCallEvents([]);
                  setIsBotSpeaking(false);
                  setIsUserSpeaking(false);
                  setBotAudioLevel(0);
                  setUserAudioLevel(0);
                  setCallAccepted(false);
                  
                  setTimeout(() => startCall(), 100);
                }}>
                  <span className="material-icons">replay</span> New Call
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CallbotSupportAgent;
